import React, { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';

const AuthContext = createContext(null);

const AUTH_KEY = 'challenges_auth_user';
const ACTIVE_TENANT_KEY = 'challenges_active_tenant';
const TOKEN_KEY = 'auth_token';
const DEV_BYPASS_KEY = 'challenges_dev_bypass';

function getCookie(name) {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp('(^|;)\\s*' + name + '=([^;]*)'));
  return match ? decodeURIComponent(match[2]) : null;
}

function setCookie(name, value, days = 7) {
  if (typeof document === 'undefined') return;
  const hostname = window.location.hostname || '';
  if (!hostname.includes('mymotivio.com')) return;
  const maxAge = days * 24 * 60 * 60;
  document.cookie = `${name}=${encodeURIComponent(value)}; domain=.mymotivio.com; path=/; max-age=${maxAge}; SameSite=Lax`;
}

function clearCookie(name) {
  if (typeof document === 'undefined') return;
  const hostname = window.location.hostname || '';
  if (!hostname.includes('mymotivio.com')) return;
  document.cookie = `${name}=; domain=.mymotivio.com; path=/; max-age=0`;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [activeTenantId, setActiveTenantIdState] = useState(null);
  const [resolvedTenantId, setResolvedTenantId] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Resolve tenant from hostname, e.g. zabka.mymotivio.com
  useEffect(() => {
    const host = window.location.hostname || '';
    const ignoredHosts = ['localhost', 'challenges-panel.pages.dev', 'main.challenges-panel.pages.dev', 'mymotivio.com'];
    const ignoredSlugs = ['www', 'console', 'panel', 'app', 'operator'];
    const slug = host.split('.')[0];
    if (!slug || ignoredHosts.includes(host) || ignoredSlugs.includes(slug)) {
      setLoading(false);
      return;
    }

    api.get(`/api/tenants/lookup?slug=${encodeURIComponent(slug)}`)
      .then((tenant) => {
        setResolvedTenantId(tenant.id);
      })
      .catch(() => {
        // ignore lookup errors; tenant may not exist
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    try {
      const storedUser = localStorage.getItem(AUTH_KEY) || getCookie(AUTH_KEY);
      const storedTenant = localStorage.getItem(ACTIVE_TENANT_KEY) || getCookie(ACTIVE_TENANT_KEY);
      const hostname = window.location.hostname || '';
      const isConsoleHost = hostname.startsWith('console.');
      if (storedUser) {
        const parsed = JSON.parse(storedUser);
        if (isConsoleHost && parsed.role !== 'platformadmin') {
          localStorage.removeItem(AUTH_KEY);
          localStorage.removeItem(ACTIVE_TENANT_KEY);
          clearCookie(AUTH_KEY);
          clearCookie(ACTIVE_TENANT_KEY);
        } else {
          setUser(parsed);
        }
      }
      if (storedTenant && !isConsoleHost) {
        setActiveTenantIdState(storedTenant);
      } else if (resolvedTenantId && !isConsoleHost) {
        setActiveTenantIdState(resolvedTenantId);
        localStorage.setItem(ACTIVE_TENANT_KEY, resolvedTenantId);
        setCookie(ACTIVE_TENANT_KEY, resolvedTenantId);
      }
    } catch {
      // ignore parse errors
    }
  }, [resolvedTenantId]);

  const isPlatformAdmin = user?.role === 'platformadmin';
  const isTenantAdmin = ['admin', 'hr', 'owner'].includes(user?.role);
  const isEmployee = user?.role === 'employee';

  const login = (authResult) => {
    // Support either { token, user } or plain user object (legacy dev)
    const token = authResult?.token;
    const authUser = authResult?.user || authResult;

    const isOperator = authUser.role === 'platformadmin';
    const normalized = {
      uid: authUser.uid,
      email: authUser.email || '',
      displayName: authUser.displayName || authUser.email?.split('@')[0] || '',
      role: authUser.role,
      tenantId: isOperator ? null : (authUser.tenantId || resolvedTenantId || 'tenant_acme'),
    };
    setUser(normalized);
    localStorage.setItem(AUTH_KEY, JSON.stringify(normalized));
    setCookie(AUTH_KEY, JSON.stringify(normalized));

    if (token) {
      localStorage.setItem(TOKEN_KEY, token);
      setCookie(TOKEN_KEY, token);
    }

    // Keep dev bypass as fallback until token auth is fully propagated
    if (isOperator) {
      const devBypass = 'poc-2025-platformadmin';
      localStorage.setItem(DEV_BYPASS_KEY, devBypass);
      setCookie(DEV_BYPASS_KEY, devBypass);
      setActiveTenantIdState(null);
      localStorage.removeItem(ACTIVE_TENANT_KEY);
      clearCookie(ACTIVE_TENANT_KEY);
      navigate('/console/clients');
    } else {
      const devBypass = 'poc-2025';
      const tenant = normalized.tenantId || activeTenantId || resolvedTenantId || 'tenant_acme';
      localStorage.setItem(DEV_BYPASS_KEY, devBypass);
      setCookie(DEV_BYPASS_KEY, devBypass);
      setActiveTenantIdState(tenant);
      localStorage.setItem(ACTIVE_TENANT_KEY, tenant);
      setCookie(ACTIVE_TENANT_KEY, tenant);
      navigate(`/${tenant}/campaigns`);
    }
  };

  const logout = () => {
    setUser(null);
    setActiveTenantIdState(null);
    localStorage.removeItem(AUTH_KEY);
    localStorage.removeItem(ACTIVE_TENANT_KEY);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(DEV_BYPASS_KEY);
    localStorage.removeItem('tenant_id');
    clearCookie(AUTH_KEY);
    clearCookie(ACTIVE_TENANT_KEY);
    clearCookie(TOKEN_KEY);
    clearCookie(DEV_BYPASS_KEY);
    navigate('/login');
  };

  const setActiveTenantId = (tenantId) => {
    if (tenantId) {
      setActiveTenantIdState(tenantId);
      localStorage.setItem(ACTIVE_TENANT_KEY, tenantId);
      setCookie(ACTIVE_TENANT_KEY, tenantId);
    } else {
      setActiveTenantIdState(null);
      localStorage.removeItem(ACTIVE_TENANT_KEY);
      clearCookie(ACTIVE_TENANT_KEY);
    }
  };

  const enterTenant = (tenantId) => {
    setActiveTenantId(tenantId);
    const hostname = window.location.hostname || '';
    const panelHostname = hostname.startsWith('console.') ? hostname.replace('console.', 'panel.') : hostname;
    const panelUrl = `${window.location.protocol}//${panelHostname}/${tenantId}/campaigns`;
    window.location.assign(panelUrl);
  };

  const exitTenant = () => {
    setActiveTenantId(null);
    const hostname = window.location.hostname || '';
    const consoleHostname = hostname.startsWith('panel.') ? hostname.replace('panel.', 'console.') : hostname;
    const consoleUrl = `${window.location.protocol}//${consoleHostname}/console/clients`;
    window.location.assign(consoleUrl);
  };

  const value = {
    user,
    loading,
    activeTenantId,
    isPlatformAdmin,
    isTenantAdmin,
    isEmployee,
    login,
    logout,
    setActiveTenantId,
    enterTenant,
    exitTenant,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
